import { getLocale, type Locale } from '@/lib/locale';

export const GROK_IMAGE_GENERATOR_PATH = '/image-generator';

export const GROK_IMAGE_GENERATOR_SEO_TITLE =
  'Grok Image Generator | Free Grok AI Image Generator';

export const GROK_IMAGE_GENERATOR_SEO_DESCRIPTION =
  'Create AI images from text prompts with a Grok image generator. Learn what prompts to use, when credits are required, and how to edit or animate results.';

export const GROK_IMAGE_GENERATOR_DATE_PUBLISHED = '2026-07-07';

export const GROK_IMAGE_GENERATOR_LAST_UPDATED = '2026-09-16';

export const GROK_IMAGE_GENERATOR_LAST_UPDATED_LABEL = 'September 16, 2026';

export const GROK_IMAGE_GENERATOR_KEYWORDS = [
  'grok image generator',
  'grok ai image generator',
  'can grok generate images',
  'how to generate images with grok ai',
  'is grok image generation free',
  'grok image generation',
].join(', ');

export const GROK_IMAGE_GENERATOR_QUICK_ANSWER =
  'A Grok image generator turns text prompts into AI images. Use it for thumbnails, product concepts, ads, posters, and social posts. Free use depends on account credits, model, resolution, and plan settings.';

export const GROK_IMAGE_GENERATOR_SUMMARY_POINTS = [
  'Start from a written prompt when you do not have a source image yet.',
  'Choose the model, aspect ratio, resolution, and quality before generating.',
  'Review credits before higher-resolution or premium image generations.',
] as const;

export const GROK_IMAGE_GENERATOR_EXAMPLE_GROUPS = [
  {
    title: 'Prompt-to-image concept examples',
    body: 'Start with one written idea, then use the Grok image generator to test composition, mood, lighting, and format before you spend time on manual design.',
    layout: 'wide',
    items: [
      {
        label: 'Product launch visual',
        alt: 'Suggested prompt direction for a product launch image',
        prompt:
          'A premium black smart bottle on a glossy studio surface, warm rim light, minimal background, clean commercial photography.',
        output:
          'A square product ad draft for a landing page or paid social test.',
        useCase: 'Best for product launches, ecommerce tests, and ad concepts.',
        text: 'Prompt a clean product scene, then refine the lighting and background for ads or landing pages.',
      },
      {
        label: 'Creator thumbnail',
        alt: 'Suggested prompt direction for a creator thumbnail',
        prompt:
          'A cinematic creator thumbnail about AI image generation, bold center subject, dramatic studio lighting, high contrast.',
        output:
          'A high-contrast thumbnail direction for Shorts, Reels, TikTok, or X.',
        useCase: 'Best for video hooks, social posts, and creator campaigns.',
        text: 'Generate a bold creator visual with face-safe composition, contrast, and social crop in mind.',
      },
      {
        label: 'Poster concept',
        alt: 'Suggested prompt direction for a campaign poster concept',
        prompt:
          'A futuristic city garden at sunrise, soft mist, reflective glass towers, editorial concept art, warm gold and teal palette.',
        output:
          'A moodboard-ready poster concept that can be refined in the editor.',
        useCase:
          'Best for campaign drafts, concept boards, and creative reviews.',
        text: 'Turn a plain campaign idea into a polished image direction for review and iteration.',
      },
    ],
  },
] as const;

export const GROK_IMAGE_GENERATOR_FEATURES = [
  {
    title: 'Text-to-image prompt generation',
    body: 'Describe the subject, scene, style, camera angle, lighting, and output use. The Grok image generator turns that prompt into a new AI image draft without requiring a design file first.',
  },
  {
    title: 'Style and format control',
    body: 'Use the Grok AI image generator with square, portrait, landscape, or vertical formats and match the output to thumbnails, posts, ads, product visuals, or concept boards.',
  },
  {
    title: 'Reference-friendly workflow',
    body: 'When a plain text prompt is not enough, continue into the image editor with reference images so the generated direction can become a tighter image-to-image edit.',
  },
  {
    title: 'Credit-aware creation',
    body: 'See login, credit, model, quality, and resolution prompts before generation so you can understand when Grok image generation is free and when paid credits may be required.',
  },
];

export const GROK_IMAGE_GENERATOR_STEPS = [
  {
    name: 'Write a specific image prompt',
    text: 'Start with the subject, setting, style, lighting, composition, and where the image will be used. A clear prompt gives the Grok image generator more usable direction.',
  },
  {
    name: 'Choose model and format settings',
    text: 'Pick the available model, aspect ratio, resolution, and quality level that fit the output. Use vertical for social, square for thumbnails, and landscape for hero concepts.',
  },
  {
    name: 'Generate and review the result',
    text: 'Generate the image, check subject accuracy, text, hands, brand details, and visual safety before publishing. Save the best draft to history when you want to continue later.',
  },
  {
    name: 'Refine, edit, or animate',
    text: 'Regenerate with a tighter prompt, open the result in the Grok AI image editor, or send a strong still image into the image-to-video workflow for motion.',
  },
];

export const GROK_IMAGE_GENERATOR_PROMPT_EXAMPLES = [
  {
    title: 'Product ad concept',
    input: 'A short product idea and brand mood.',
    prompt:
      'A premium black smart bottle on a glossy studio surface, warm rim light, minimal background, clean commercial photography, square ad image.',
    result:
      'A product-style visual draft for landing pages, ads, or social tests.',
  },
  {
    title: 'Social thumbnail',
    input: 'A creator topic or video hook.',
    prompt:
      'A cinematic creator thumbnail about AI image generation, bold center subject, dramatic neon studio lighting, high contrast, vertical crop.',
    result:
      'A high-contrast image direction for Shorts, Reels, TikTok, or X posts.',
  },
  {
    title: 'Concept art direction',
    input: 'A scene idea, character, or campaign mood.',
    prompt:
      'A futuristic city garden at sunrise, soft mist, reflective glass towers, editorial concept art, warm gold and teal color palette.',
    result:
      'A moodboard-ready concept image that can be refined with image editing.',
  },
];

export const GROK_IMAGE_GENERATOR_VALUE_ROWS = [
  {
    need: 'Start from text',
    value:
      'Use a Grok image generator when you have an idea but no source image, layout, or design file yet.',
  },
  {
    need: 'Move faster than manual mockups',
    value:
      'Generate several visual directions quickly, compare them, then keep only the best draft for editing or publishing.',
  },
  {
    need: 'Control output format',
    value:
      'Choose formats that match the channel before generation, instead of cropping an image after the subject is already misplaced.',
  },
  {
    need: 'Continue the workflow',
    value:
      'Send a good still image into the Grok AI image editor or Grok image-to-video workflow when the first image becomes the next creative asset.',
  },
];

export const GROK_IMAGE_GENERATOR_USE_CASES = [
  {
    title: 'Marketers testing campaign visuals',
    body: 'A marketer can start with a product, offer, and audience angle, generate several Grok image generator drafts, then pick the most promising image for an ad mockup or landing page hero.',
  },
  {
    title: 'Creators making thumbnails and posts',
    body: 'A creator can describe the video topic, emotion, color palette, and crop, then use the Grok AI image generator to create a thumbnail or social post image before editing details.',
  },
  {
    title: 'Founders exploring product concepts',
    body: 'A founder can turn a rough feature, app, packaging, or scene idea into quick image concepts, then share the strongest draft for feedback before commissioning final assets.',
  },
];

export const GROK_IMAGE_GENERATOR_LIMITS = [
  {
    limit: 'Prompt quality matters',
    check:
      'Include 5 prompt fields: subject, scene, style, lighting, and output use.',
    meaning:
      'Short prompts can work, but stronger results usually describe subject, style, setting, lighting, format, and intended use.',
    action:
      'Add concrete details and remove conflicting style instructions before regenerating.',
  },
  {
    limit: 'Generated details need review',
    check:
      'Inspect faces, hands, logos, fine text, labels, and factual claims.',
    meaning:
      'Faces, hands, logos, fine text, product labels, and factual details can still need manual checking.',
    action:
      'Review every image before publishing, especially for ads, products, or client work.',
  },
  {
    limit: 'Free access can depend on credits',
    check:
      'Check account credits, selected model, resolution, quality, and plan before generation.',
    meaning:
      'Grok image generation uses credits from one-time packs. The model, resolution, and quality you pick change the credit cost.',
    action:
      'Sign in, check the visible credit prompt, and review pricing before higher-cost generations.',
  },
  {
    limit: 'Safety and rights rules still apply',
    check:
      'Use rights-cleared prompts and review copyright, privacy, impersonation, and platform rules.',
    meaning:
      'The tool does not remove legal, copyright, privacy, impersonation, or platform-safety obligations.',
    action:
      'Use rights-cleared prompts and avoid unsafe, deceptive, infringing, or non-consensual uses.',
  },
];

export const GROK_IMAGE_GENERATOR_FAQS = [
  {
    question: 'What is a Grok image generator?',
    answer:
      'A Grok image generator turns a text prompt into a new AI image. Use this Grok image generator for thumbnails, product concepts, ads, posters, and social posts, then continue into the editor or Grok image to video if the still frame is ready.',
  },
  {
    question: 'Can Grok generate images?',
    answer:
      'Yes. This Grok image generator creates AI images from text prompts when image generation is available in the workspace. It is built for prompt-to-image creation, then lets you continue into editing, history, or image-to-video workflows.',
  },
  {
    question: 'How to generate images with Grok AI?',
    answer:
      'Write a clear prompt, choose the model and output format, then click generate. For better Grok AI image generator results, include the subject, scene, style, lighting, composition, and where the image will be used.',
  },
  {
    question: 'Is Grok image generation free?',
    answer:
      'No. Each image uses credits from a one-time credit pack, and there are no free sign-up credits. The credit cost is shown before you generate, and credits for a failed generation are refunded.',
  },
  {
    question:
      'What is the difference between a Grok image generator and a Grok image editor?',
    answer:
      'A Grok image generator starts from text and creates a new image. A Grok image editor starts from an existing image or generated draft and changes the background, style, subject details, or composition with a prompt.',
  },
  {
    question: 'Can I use generated Grok images commercially?',
    answer:
      'Commercial use depends on the terms of the tool, model provider, plan, prompt, and source material. Review rights, brand permissions, and platform rules before using generated images in ads, ecommerce, client work, or public campaigns.',
  },
  {
    question: 'What should I do if the Grok image generator hits a limit?',
    answer:
      'If official Grok shows image limit reached or another Grok Imagine limit, save the last usable draft here, then read the Grok Imagine limit FAQ or compare a Grok Imagine alternative instead of repeating the same prompt.',
  },
];

export const GROK_IMAGE_GENERATOR_SCHEMA_FEATURES = [
  'Text-to-image generation from natural language prompts',
  'Model, aspect ratio, resolution, and quality controls',
  'Prompt examples for product, social, campaign, and concept visuals',
  'Credit-aware generation flow with sign-in and upgrade prompts',
  'Follow-up editing, history, and image-to-video workflows',
];

export const GROK_IMAGE_GENERATOR_DATA = {
  path: GROK_IMAGE_GENERATOR_PATH,
  seoTitle: GROK_IMAGE_GENERATOR_SEO_TITLE,
  seoDescription: GROK_IMAGE_GENERATOR_SEO_DESCRIPTION,
  datePublished: GROK_IMAGE_GENERATOR_DATE_PUBLISHED,
  lastUpdated: GROK_IMAGE_GENERATOR_LAST_UPDATED,
  lastUpdatedLabel: GROK_IMAGE_GENERATOR_LAST_UPDATED_LABEL,
  keywords: GROK_IMAGE_GENERATOR_KEYWORDS,
  quickAnswer: GROK_IMAGE_GENERATOR_QUICK_ANSWER,
  summaryPoints: GROK_IMAGE_GENERATOR_SUMMARY_POINTS,
  visualExampleGroups: GROK_IMAGE_GENERATOR_EXAMPLE_GROUPS,
  features: GROK_IMAGE_GENERATOR_FEATURES,
  steps: GROK_IMAGE_GENERATOR_STEPS,
  examples: GROK_IMAGE_GENERATOR_PROMPT_EXAMPLES,
  valueRows: GROK_IMAGE_GENERATOR_VALUE_ROWS,
  useCases: GROK_IMAGE_GENERATOR_USE_CASES,
  limits: GROK_IMAGE_GENERATOR_LIMITS,
  faqs: GROK_IMAGE_GENERATOR_FAQS,
  schemaFeatures: GROK_IMAGE_GENERATOR_SCHEMA_FEATURES,
  copy: {
    hero: {
      eyebrow: 'Start here',
      title: 'What is a Grok Image Generator?',
      detailsTitle: 'What you can do',
      takeawaysTitle: 'Quick takeaways',
      details: [
        {
          label: 'Create',
          value: 'New AI images from a written prompt',
        },
        {
          label: 'Use it for',
          value: 'Thumbnails, product concepts, ads, posters, and social posts',
        },
        {
          label: 'Best prompt',
          value:
            'Subject, scene, style, lighting, format, and where the image will be used',
        },
        {
          label: 'Cost',
          value: 'Credits from a one-time pack, shown before you generate',
        },
        {
          label: 'Last updated',
          value: GROK_IMAGE_GENERATOR_LAST_UPDATED_LABEL,
        },
      ],
    },
    examples: {
      eyebrow: 'Example outputs',
      title: 'Example outputs from Grok image prompts',
      description:
        'Each example pairs a prompt direction with the expected output and the job it helps finish.',
      headers: {
        prompt: 'Prompt',
        output: 'Output',
        useCase: 'Use case',
      },
    },
    why: {
      eyebrow: 'Why this tool',
      title: 'Why use a Grok image generator?',
      description:
        'A Grok image generator is useful when the first task is not editing an existing file, but turning a written idea into a usable image draft quickly.',
      cards: [
        {
          title: 'Built for prompt-first creation',
          body: 'The Grok AI image generator lets you start from a written prompt, choose output settings, and create a new image without needing a source photo.',
        },
        {
          title: 'Connected to the next creative step',
          body: 'After image generation, the same Grok workspace can support editing, history review, downloads, or image-to-video when a still image is ready for motion.',
        },
      ],
    },
    features: {
      eyebrow: 'What it can do',
      title: 'Core features of the Grok AI image generator',
      description:
        'The Grok image generator keeps prompt writing, model selection, image format, credit checks, and follow-up editing close together so creators can move from idea to asset.',
    },
    steps: {
      eyebrow: 'How it works',
      title: 'How to generate images with Grok AI',
      description:
        'The best Grok AI image generator results come from a prompt that says what the image should show, how it should look, and where it will be used.',
    },
    promptExamples: {
      eyebrow: 'Prompt examples',
      title: 'Prompt examples for Grok image generation',
      description:
        'Use these prompts as starting points, then adjust subject, style, lighting, crop, and output use for your own Grok image generator workflow.',
      headers: {
        useCase: 'Use case',
        input: 'Starting input',
        prompt: 'Generation prompt',
        result: 'Expected result',
      },
    },
    useCases: {
      eyebrow: 'Use cases',
      title: 'Where Grok image generation fits best',
      description:
        'Use this page when you need a new AI image first, then decide whether to edit, download, animate, or regenerate it.',
    },
    value: {
      eyebrow: 'Value',
      title: 'What this Grok AI image generator helps you do',
      headers: {
        need: 'Need',
        helps: 'How it helps',
      },
    },
    limits: {
      eyebrow: 'Limits',
      title: 'Best inputs and realistic limits',
      headers: {
        limit: 'Limit',
        check: 'Check before generating',
        meaning: 'What it means',
        action: 'Recommended action',
      },
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Grok image generator FAQ',
      description:
        'Direct answers about whether Grok can generate images, how to generate images with Grok AI, free access, editing differences, and commercial review.',
    },
    cta: {
      eyebrow: 'Ready to create an image?',
      title: 'Write a prompt and start generating Grok-style AI images online.',
      generate: 'Generate image',
      edit: 'Edit an image',
      animate: 'Animate image',
      limit: 'Grok Imagine limit FAQ',
      alternative: 'Grok Imagine alternative',
    },
    schema: {
      softwareName: 'Grok Image Generator',
      actionName: 'Generate an AI image',
      resultName: 'AI-generated image',
      offerDescription:
        'Uses credits from one-time credit packs. The credit cost is shown before each generation.',
      howToName: 'How to generate images with Grok AI',
      howToDescription:
        'Write a prompt, choose model and image settings, generate the image, then refine, edit, or animate the result.',
      breadcrumbHome: 'Home',
      breadcrumbPage: 'Grok Image Generator',
    },
  },
};

type GrokImageGeneratorSeoData = typeof GROK_IMAGE_GENERATOR_DATA;

const ES_GROK_IMAGE_GENERATOR_DATA = {
  ...GROK_IMAGE_GENERATOR_DATA,
  seoTitle: 'Generador de imágenes Grok | Crear imágenes IA con Grok',
  seoDescription:
    'Crea imágenes IA desde prompts de texto con un generador de imágenes Grok. Aprende qué prompt usar, cuándo se requieren créditos y cómo editar o animar el resultado.',
  lastUpdatedLabel: '7 de julio de 2026',
  keywords: [
    'generador de imágenes Grok',
    'generador de imágenes IA Grok',
    'Grok generador de imagenes',
    'Grok puede generar imágenes',
    'crear imágenes con Grok',
    'Grok generador de imágenes gratis',
    'Grok AI image generator',
  ].join(', '),
  quickAnswer:
    'Un generador de imágenes Grok convierte prompts de texto en imágenes IA. Úsalo para miniaturas, conceptos de producto, anuncios, pósters y publicaciones sociales. El uso gratuito depende de créditos, modelo, resolución, calidad y plan.',
  summaryPoints: [
    'Empieza con un prompt escrito cuando todavía no tienes una imagen fuente.',
    'Elige modelo, relación de aspecto, resolución y calidad antes de generar.',
    'Revisa los créditos antes de generar imágenes premium o de mayor resolución.',
  ],
  visualExampleGroups: [
    {
      title: 'Ejemplos de concepto prompt a imagen',
      body: 'Empieza con una idea escrita y usa el generador de imágenes Grok para probar composición, ambiente, iluminación y formato antes de invertir tiempo en diseño manual.',
      layout: 'wide',
      items: [
        {
          label: 'Visual de lanzamiento de producto',
          alt: 'Dirección de prompt para una imagen de lanzamiento de producto',
          prompt:
            'Una botella inteligente negra premium sobre una superficie de estudio brillante, luz cálida de contorno, fondo minimalista, fotografía comercial limpia.',
          output:
            'Un borrador cuadrado de anuncio de producto para landing page o prueba social pagada.',
          useCase:
            'Ideal para lanzamientos, ecommerce y conceptos de anuncios.',
          text: 'Genera una escena de producto limpia y ajusta iluminación y fondo para anuncios o landing pages.',
        },
        {
          label: 'Miniatura para creador',
          alt: 'Dirección de prompt para una miniatura de creador',
          prompt:
            'Miniatura cinemática sobre generación de imágenes IA, sujeto central fuerte, iluminación dramática de estudio, alto contraste.',
          output:
            'Una dirección de miniatura de alto contraste para Shorts, Reels, TikTok o X.',
          useCase:
            'Ideal para hooks de video, posts sociales y campañas de creador.',
          text: 'Crea un visual potente pensando en encuadre de rostro, contraste y recorte social.',
        },
        {
          label: 'Concepto de póster',
          alt: 'Dirección de prompt para un póster de campaña',
          prompt:
            'Jardín urbano futurista al amanecer, niebla suave, torres de vidrio reflectante, arte conceptual editorial, paleta dorada y verde azulado.',
          output:
            'Un concepto listo para moodboard que puede refinarse en el editor.',
          useCase:
            'Ideal para campañas, tableros de concepto y revisión creativa.',
          text: 'Convierte una idea de campaña simple en una dirección visual pulida.',
        },
      ],
    },
  ],
  features: [
    {
      title: 'Generación texto a imagen',
      body: 'Describe sujeto, escena, estilo, cámara, iluminación y uso final. El generador de imágenes Grok convierte ese prompt en un nuevo borrador de imagen IA sin partir de un archivo de diseño.',
    },
    {
      title: 'Control de estilo y formato',
      body: 'Usa Grok con formatos cuadrado, retrato, paisaje o vertical y adapta la salida a miniaturas, posts, anuncios, producto o tableros de concepto.',
    },
    {
      title: 'Flujo listo para referencias',
      body: 'Cuando un prompt de texto no alcanza, continúa en el editor de imágenes con referencias para convertir la dirección generada en una edición más precisa.',
    },
    {
      title: 'Creación consciente de créditos',
      body: 'Consulta avisos de inicio de sesión, créditos, modelo, calidad y resolución antes de generar para entender cuándo se puede probar gratis y cuándo se requieren créditos.',
    },
  ],
  steps: [
    {
      name: 'Escribe un prompt específico',
      text: 'Empieza con sujeto, entorno, estilo, iluminación, composición y uso final. Un prompt claro da al generador Grok una dirección más útil.',
    },
    {
      name: 'Elige modelo y formato',
      text: 'Selecciona modelo, relación de aspecto, resolución y calidad según el canal. Usa vertical para social, cuadrado para miniaturas y paisaje para héroes visuales.',
    },
    {
      name: 'Genera y revisa el resultado',
      text: 'Genera la imagen y comprueba sujeto, texto, manos, detalles de marca y seguridad visual antes de publicarla.',
    },
    {
      name: 'Refina, edita o anima',
      text: 'Regenera con un prompt más preciso, abre el resultado en el editor Grok o envía una buena imagen fija al flujo image-to-video.',
    },
  ],
  examples: [
    {
      title: 'Concepto de anuncio de producto',
      input: 'Una idea breve de producto y tono de marca.',
      prompt:
        'Una botella inteligente negra premium sobre una superficie de estudio brillante, luz cálida de contorno, fondo minimalista, fotografía comercial limpia, imagen cuadrada de anuncio.',
      result:
        'Un borrador visual de producto para landing pages, anuncios o pruebas sociales.',
    },
    {
      title: 'Miniatura social',
      input: 'Un tema de creador o hook de video.',
      prompt:
        'Miniatura cinemática sobre generación de imágenes IA, sujeto central fuerte, iluminación de estudio neón, alto contraste, recorte vertical.',
      result:
        'Una dirección de imagen de alto contraste para Shorts, Reels, TikTok o X.',
    },
    {
      title: 'Dirección de concept art',
      input: 'Una idea de escena, personaje o mood de campaña.',
      prompt:
        'Jardín urbano futurista al amanecer, niebla suave, torres de vidrio reflectante, arte conceptual editorial, paleta cálida dorada y verde azulado.',
      result:
        'Una imagen de concepto lista para moodboard que puede refinarse con edición.',
    },
  ],
  valueRows: [
    {
      need: 'Empezar desde texto',
      value:
        'Usa un generador de imágenes Grok cuando tienes una idea pero todavía no tienes imagen fuente, layout o archivo de diseño.',
    },
    {
      need: 'Avanzar más rápido que con mockups manuales',
      value:
        'Genera varias direcciones visuales, compáralas y conserva solo el mejor borrador para editar o publicar.',
    },
    {
      need: 'Controlar el formato',
      value:
        'Elige el formato del canal antes de generar, en lugar de recortar después una imagen con el sujeto mal colocado.',
    },
    {
      need: 'Continuar el flujo',
      value:
        'Envía una buena imagen fija al editor Grok o al flujo Grok image-to-video cuando el primer resultado se convierte en el siguiente asset.',
    },
  ],
  useCases: [
    {
      title: 'Marketers que prueban visuales de campaña',
      body: 'Un marketer puede partir de producto, oferta y audiencia, generar varios borradores Grok y elegir la imagen más prometedora para un anuncio o hero de landing.',
    },
    {
      title: 'Creadores que preparan miniaturas y posts',
      body: 'Un creador puede describir tema, emoción, paleta y recorte para generar una miniatura o post antes de editar detalles.',
    },
    {
      title: 'Founders explorando conceptos de producto',
      body: 'Un founder puede convertir una idea de función, app, packaging o escena en conceptos rápidos y compartir el mejor borrador antes de producir assets finales.',
    },
  ],
  limits: [
    {
      limit: 'La calidad del prompt importa',
      check: 'Incluye sujeto, escena, estilo, iluminación y uso final.',
      meaning:
        'Los prompts cortos pueden funcionar, pero los mejores resultados suelen describir sujeto, estilo, entorno, luz, formato y destino.',
      action:
        'Añade detalles concretos y elimina instrucciones de estilo contradictorias antes de regenerar.',
    },
    {
      limit: 'Los detalles generados deben revisarse',
      check:
        'Revisa rostros, manos, logos, texto pequeño, etiquetas y afirmaciones factuales.',
      meaning:
        'Rostros, manos, logos, texto fino, etiquetas de producto y detalles factuales todavía pueden necesitar revisión manual.',
      action:
        'Revisa cada imagen antes de usarla en anuncios, producto o trabajo de cliente.',
    },
    {
      limit: 'El acceso gratis puede depender de créditos',
      check:
        'Comprueba créditos, modelo, resolución, calidad y plan antes de generar.',
      meaning:
        'La generación puede probarse gratis cuando hay créditos disponibles, pero modelo, resolución, cuenta y plan pueden cambiar lo que es gratis.',
      action:
        'Inicia sesión, revisa el aviso visible de créditos y consulta precios antes de generaciones de mayor coste.',
    },
    {
      limit: 'Siguen aplicando reglas de seguridad y derechos',
      check:
        'Usa prompts con derechos claros y revisa copyright, privacidad, suplantación y reglas de plataforma.',
      meaning:
        'La herramienta no elimina obligaciones legales, de copyright, privacidad, suplantación o seguridad.',
      action:
        'Usa entradas con derechos claros y evita usos inseguros, engañosos, infractores o no consentidos.',
    },
  ],
  faqs: [
    {
      question: '¿Grok puede generar imágenes?',
      answer:
        'Sí. Este generador de imágenes Grok crea imágenes IA desde prompts de texto cuando la generación está disponible en el workspace. Después puedes continuar hacia edición, historial o image-to-video.',
    },
    {
      question: '¿Cómo generar imágenes con Grok AI?',
      answer:
        'Escribe un prompt claro, elige modelo y formato, y genera la imagen. Para mejores resultados, incluye sujeto, escena, estilo, iluminación, composición y uso final.',
    },
    {
      question: '¿El generador de imágenes Grok es gratis?',
      answer:
        'Solo es gratis de probar cuando tu cuenta tiene créditos gratuitos o una asignación activa. Salidas de mayor resolución, modelos premium o créditos agotados pueden requerir créditos pagados.',
    },
    {
      question:
        '¿Cuál es la diferencia entre generador y editor de imágenes Grok?',
      answer:
        'El generador empieza desde texto y crea una imagen nueva. El editor parte de una imagen existente o un borrador generado y cambia fondo, estilo, detalles o composición con un prompt.',
    },
    {
      question: '¿Puedo usar imágenes de Grok comercialmente?',
      answer:
        'El uso comercial depende de términos del producto, proveedor del modelo, plan, prompt y material de origen. Revisa derechos, permisos de marca y reglas de plataforma antes de usar imágenes en anuncios, ecommerce o trabajo de cliente.',
    },
  ],
  schemaFeatures: [
    'Generación texto a imagen desde prompts naturales',
    'Controles de modelo, relación de aspecto, resolución y calidad',
    'Ejemplos de prompts para producto, social, campañas y conceptos',
    'Flujo consciente de créditos con avisos de inicio de sesión y upgrade',
    'Edición posterior, historial y flujos image-to-video',
  ],
  copy: {
    ...GROK_IMAGE_GENERATOR_DATA.copy,
    hero: {
      eyebrow: 'Empieza aquí',
      title: 'Generador de imágenes Grok para crear imágenes IA',
      detailsTitle: 'Qué puedes hacer',
      takeawaysTitle: 'Resumen rápido',
      details: [
        { label: 'Crear', value: 'Nuevas imágenes IA desde un prompt escrito' },
        {
          label: 'Úsalo para',
          value: 'Miniaturas, conceptos de producto, anuncios, pósters y posts',
        },
        {
          label: 'Mejor prompt',
          value: 'Sujeto, escena, estilo, luz, formato y uso final',
        },
        {
          label: 'Prueba gratis',
          value:
            'Disponible cuando la cuenta tiene créditos o allowance gratis',
        },
        { label: 'Actualizado', value: '7 de julio de 2026' },
      ],
    },
    examples: {
      eyebrow: 'Ejemplos de salida',
      title: 'Ejemplos de imágenes desde prompts Grok',
      description:
        'Cada ejemplo combina una dirección de prompt, la salida esperada y el trabajo que ayuda a terminar.',
      headers: {
        prompt: 'Prompt',
        output: 'Salida',
        useCase: 'Caso de uso',
      },
    },
    why: {
      eyebrow: 'Por qué este tool',
      title: '¿Por qué usar un generador de imágenes Grok?',
      description:
        'Es útil cuando la primera tarea no es editar un archivo existente, sino convertir una idea escrita en un borrador visual utilizable.',
      cards: [
        {
          title: 'Creación prompt-first',
          body: 'El generador de imágenes IA Grok permite empezar desde texto, elegir ajustes y crear una imagen nueva sin necesitar una foto fuente.',
        },
        {
          title: 'Conectado al siguiente paso creativo',
          body: 'Después de generar, el mismo workspace puede apoyar edición, historial, descargas o image-to-video cuando una imagen fija ya está lista.',
        },
      ],
    },
    features: {
      eyebrow: 'Qué puede hacer',
      title: 'Funciones clave del generador de imágenes IA Grok',
      description:
        'Prompt, modelo, formato, revisión de créditos y edición posterior están cerca para pasar de idea a asset.',
    },
    steps: {
      eyebrow: 'Cómo funciona',
      title: 'Cómo generar imágenes con Grok AI',
      description:
        'Los mejores resultados vienen de un prompt que dice qué debe mostrar la imagen, cómo debe verse y dónde se usará.',
    },
    promptExamples: {
      eyebrow: 'Ejemplos de prompt',
      title: 'Prompts para generación de imágenes Grok',
      description:
        'Usa estos prompts como punto de partida y ajusta sujeto, estilo, iluminación, recorte y uso final.',
      headers: {
        useCase: 'Caso de uso',
        input: 'Entrada inicial',
        prompt: 'Prompt de generación',
        result: 'Resultado esperado',
      },
    },
    useCases: {
      eyebrow: 'Casos de uso',
      title: 'Dónde encaja mejor la generación de imágenes Grok',
      description:
        'Usa esta página cuando necesitas primero una imagen IA y luego decidir si editarla, descargarla, animarla o regenerarla.',
    },
    value: {
      eyebrow: 'Valor',
      title: 'Qué te ayuda a hacer este generador de imágenes IA Grok',
      headers: { need: 'Necesidad', helps: 'Cómo ayuda' },
    },
    limits: {
      eyebrow: 'Límites',
      title: 'Mejores entradas y límites realistas',
      headers: {
        limit: 'Límite',
        check: 'Revisar antes de generar',
        meaning: 'Qué significa',
        action: 'Acción recomendada',
      },
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'FAQ del generador de imágenes Grok',
      description:
        'Respuestas directas sobre si Grok puede generar imágenes, cómo usarlo, acceso gratis, diferencias con el editor y revisión comercial.',
    },
    cta: {
      eyebrow: '¿Listo para crear una imagen?',
      title:
        'Escribe un prompt y empieza a generar imágenes IA estilo Grok online.',
      generate: 'Generar imagen',
      edit: 'Editar una imagen',
      animate: 'Animar imagen',
      limit: 'FAQ de límite Grok Imagine',
      alternative: 'Alternativa a Grok Imagine',
    },
    schema: {
      softwareName: 'Generador de imágenes Grok',
      actionName: 'Generar una imagen IA',
      resultName: 'Imagen generada por IA',
      offerDescription:
        'Usa créditos de paquetes de pago único. El coste en créditos se muestra antes de cada generación.',
      howToName: 'Cómo generar imágenes con Grok AI',
      howToDescription:
        'Escribe un prompt, elige modelo y ajustes de imagen, genera la imagen y luego refina, edita o anima el resultado.',
      breadcrumbHome: 'Inicio',
      breadcrumbPage: 'Generador de imágenes Grok',
    },
  },
} satisfies GrokImageGeneratorSeoData;

export function getGrokImageGeneratorSeoData(
  locale: Locale = getLocale()
): GrokImageGeneratorSeoData {
  if (locale === 'es') return ES_GROK_IMAGE_GENERATOR_DATA;
  return GROK_IMAGE_GENERATOR_DATA;
}
